import { IconButton, Tooltip } from '@mui/material';

import { IcPopUp } from '@/assets/images';
import { IAccount, IUser } from '@/models';
import { validateRoute } from '@/services';
import { useToastStore } from '@/store';

/**
 * Permission-aware "open in new tab" action used in Related Records tables. When the user
 * lacks access to the target route, the IconButton is disabled, the tooltip surfaces the
 * reason on hover, and a click (which only fires while enabled) opens the URL.
 *
 * Use this anywhere a related-record row offers a click-out to a route the current user
 * may or may not have permission to view — Person / CFS / Internal Affairs related-records
 * tables, etc.
 */
export const OpenInNewTabAction = ({
  path,
  canOpen,
}: {
  path: string;
  canOpen: boolean;
}) => {
  const { updateToast } = useToastStore();

  if (!path) return null;

  return (
    <Tooltip
      title={!canOpen ? 'You do not have permission to open this record.' : ''}
      disableHoverListener={canOpen}
      disableFocusListener={canOpen}
      disableTouchListener={canOpen}
    >
      <span>
        <IconButton
          disabled={!canOpen}
          onClick={(event) => {
            event.stopPropagation();
            if (!canOpen) {
              updateToast({
                open: true,
                message: 'You do not have permission to open this record.',
              });
              return;
            }
            window.open(path, '_blank', 'noopener');
          }}
        >
          <IcPopUp className="text-gray-400" />
        </IconButton>
      </span>
    </Tooltip>
  );
};

export const canOpenPath = (
  path: string,
  user?: IUser | null,
  account?: IAccount | null,
) => {
  if (!path) return false;
  return validateRoute(path, user ?? undefined, account ?? undefined);
};
